import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { apiError } from "@/lib/api";
import { Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const { project } = useProject();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Hero */}
      <div className="relative hidden w-1/2 lg:block">
        <img
          src={project.imageUrl}
          alt={`${project.name} - Horoz Group`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/45 to-primary/25" />
        <div className="absolute bottom-0 left-0 p-12 text-primary-foreground">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-white/15">
            <Building2 className="h-6 w-6" />
          </div>
          <p className="overline mb-1 text-white/60">Şantiye Projesi · Horoz Group</p>
          <h1 className="font-heading text-5xl font-bold leading-none tracking-tight">{project.name}</h1>
          <p className="mt-3 max-w-md text-white/70">
            Şantiye giderlerinizi tek bir yerden takip edin. Hızlı, güvenli ve şeffaf.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Building2 className="h-6 w-6" />
            </div>
          </div>
          <p className="overline mb-2">Giriş</p>
          <h2 className="font-heading text-3xl font-bold">Hoş geldiniz</h2>
          <p className="mt-2 text-sm text-muted-foreground">Devam etmek için giriş yapın.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@santiye.com"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Giriş Yap"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
